// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";

interface StatusIndicatorProps {
  active: boolean;
  deleted?: boolean;
  className?: string;
}

const STYLES = {
  deleted: "bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-900",
  active:
    "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-900",
  inactive: "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-900 dark:text-gray-400 dark:border-gray-800",
} as const;

type StatusKey = "deleted" | "active" | "inactive";

function getStatusKey(active: boolean, deleted?: boolean): StatusKey {
  if (deleted) return "deleted";
  if (active) return "active";
  return "inactive";
}

export function StatusIndicator({ active, deleted, className }: StatusIndicatorProps) {
  const { t } = useTranslation("general");
  const key = getStatusKey(active, deleted);

  return (
    <Badge variant="outline" className={`font-normal ${STYLES[key]} ${className ?? ""}`}>
      {t(`status.${key}`)}
    </Badge>
  );
}
